import React from "react";

import { connect } from "react-redux";
import { MultiSelect as Component } from "../MultiSelect";
import {
  mscqSubmit,
  mscqSelectOption,
  mscqEnableAll,
  mscqReset,
} from "@learningblocks/exp-redux";
import { fetchTranslations } from "../locales/config";
import TranslationsProvider from "../../TranslationsProvider";

/**
 * mapStateToProps - This function maps the state from the Redux store to the component's props.
 * It allows us to extract specific data from the Redux store state and use them as props within our React component.
 *
 * @param {Object} state - The entire state of the Redux store.
 * @param {Object} ownProps - The current props of the component.
 *
 * @returns {Object} - Returns an object which populates the props of our component.
 */
function mapStateToProps(state, ownProps) {
  // Find the entry in the store state using the provided id.

  const entry = state.mscq[ownProps.id];

  if (!entry) {
    // If no matching entry is found, return the original ownProps.
    return ownProps;
  }

  const disabledList = entry.disabled || [];
  const hasAttempted = entry.hasAttempted || false;
  const totalAttempts = entry.attemptsCount
    ? ownProps.totalAttempts - entry.attemptsCount
    : ownProps.totalAttempts;

  // When showAttempts is false (hidden), attempts are unlimited - don't force completion at 0
  const attemptsExhausted = ownProps.showAttempts && totalAttempts === 0;

  // Check if all correct answers are disabled
  const allCorrectAnswersDisabled = ownProps.answerId.every((element) =>
    disabledList.includes(element)
  );

  // Check if ONLY correct answers are disabled (no incorrect answers)
  const onlyCorrectAnswersDisabled = disabledList.every((element) =>
    ownProps.answerId.includes(element)
  );

  const complete =
    (allCorrectAnswersDisabled && onlyCorrectAnswersDisabled) ||
    attemptsExhausted;

  const updatedCheckboxes = ownProps.checkboxes.map((checkbox) => {
    // If the option's id exists in the "disabled" array of the "entry", set "selected" and "disabled" to true.
    if (disabledList.includes(checkbox.id)) {
      return {
        ...checkbox,
        selected: true,
        disabled: true,
      };
    } else if (entry.selected.includes(checkbox.id) && !complete) {
      return {
        ...checkbox,
        selected: true,
        disabled: false,
      };
    }
    // Explicitly clear selected state if not in Redux state
    return {
      ...checkbox,
      selected: false,
      disabled: false,
    };
  });

  return {
    ...ownProps,
    checkboxes: updatedCheckboxes,
    totalAttempts: totalAttempts,
    complete: complete,
    hasAttempted: hasAttempted,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    selectOptionRedux: (params) => {
      dispatch(mscqSelectOption(params));
    },
    submitAnswerRedux: (id) => {
      dispatch(mscqSubmit(id));
    },
    resetAnswerRedux: (id) => {
      dispatch(mscqReset(id));
    },
    enableAllOptions: (id) => {
      dispatch(mscqEnableAll(id));
    },
    dispatch,
  };
}

function mergeProps(stateProps, dispatchProps, ownProps) {
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    submitAnswer: (id) => {
      if (ownProps.submitAnswer) {
        ownProps.submitAnswer(id);
      }
      dispatchProps.submitAnswerRedux(id);
    },
    resetAnswer: (id) => {
      if (ownProps.resetAnswer) {
        ownProps.resetAnswer(id);
      }
      dispatchProps.resetAnswerRedux(id);
    },
    selectOption: (id) => {
      if (ownProps.submitAnswer) {
        ownProps.selectOption(id);
      }
      dispatchProps.selectOptionRedux(id);
    },
  };
}

// Connect our component to the Redux store.
// This HOC (Higher Order Component) provides our component with props from the store state (mapStateToProps) and dispatch actions (mapDispatchToProps).
export const MultiSelectRedux = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)((props) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <Component {...props} />
  </TranslationsProvider>
));
